import React from "react";
import { asNumber, cn } from "@/lib/utils";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useCurrentRef } from "@/hooks/use-current-ref";
import SelectInputCreatable from "../select-input/select-input-creatable";
import _ from "lodash";

type Time = { minute: number; hour: number };

// Parses free-typed input ("8", "8:30", "8 30") into a clock time, or undefined when it isn't one.
// Deliberately strict on both counts that used to leak through: blank input qualified, because
// "".split(/[:\s]/) yields [""] and asNumber("") is Number("") === 0 - a defined number - which is
// why a selectable empty entry appeared at the end of the list; and non-numeric input qualified too,
// because asNumber returns its argument unchanged when it isn't a number, so "abc" was "defined".
const parseTypedTime = (input: string): Time | undefined => {
  const parts = input
    .trim()
    .split(/[:\s]/)
    .filter((part) => part.length > 0);
  if (parts.length === 0 || parts.length > 2) return undefined;

  const [hour, minute = 0] = parts.map(Number);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return undefined;
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) return undefined;
  return { hour, minute };
};

interface TimeSelectorProps {
  disabled?: boolean;
  className?: string;
  value?: string; // HH[:\s]TT
  onChange?: (value: string | undefined) => void;
  withAmPm?: boolean;
  preferSpace?: boolean;
}

const TimeSelector: React.FC<TimeSelectorProps> = ({
  disabled = false,
  className = "",
  value,
  onChange,
  withAmPm = true,
  preferSpace,
}) => {
  const asOption = React.useCallback(
    (value: Time) => ({
      value,
      label: `${withAmPm ? value.hour % 12 || 12 : value.hour}:${_.padStart(`${value.minute}`, 2, "0")}`,
    }),
    [withAmPm],
  );

  const time = React.useMemo(() => {
    if (!value) return undefined;
    const [hour_ = "", minute_ = ""] = value.trim().split(/[:\s]/g);
    const hour = asNumber(hour_ || "0") ?? 0;
    const minute = asNumber(minute_ || "0") ?? 0;
    const meridiem = hour >= 12 ? ("PM" as const) : ("AM" as const);
    const preferSpace = value.includes(":") ? false : true;
    return { hour, minute, meridiem, preferSpace };
  }, [value]);

  const [meridiem, setMeridiem] = React.useState(time?.meridiem);
  React.useEffect(() => {
    setMeridiem(time?.meridiem);
  }, [time?.meridiem]);

  const timeOptions = React.useMemo(
    () => [...Array(24)].map((_, i) => asOption({ hour: i, minute: 0 })),
    [asOption, withAmPm],
  );
  const timeOptionsMeridiem = React.useMemo(
    () =>
      withAmPm
        ? timeOptions.filter((op) =>
            meridiem == "PM" ? op.value.hour >= 12 : op.value.hour < 12,
          )
        : timeOptions,
    [timeOptions, meridiem],
  );

  const onChangeRef = useCurrentRef(onChange);

  return (
    <div className={cn("flex items-center space-x-2 min-w-0", className)}>
      <SelectInputCreatable
        options={timeOptionsMeridiem}
        value={time ? asOption(time) : undefined}
        onChange={(op) => {
          if (onChangeRef.current) {
            const next = !op
              ? undefined
              : [op.value.hour, op.value.minute].join(" ");
            onChangeRef.current(next);
          }
        }}
        isMulti={false}
        isDisabled={disabled}
        className="flex-grow min-w-0"
        classNamesOptions={{ override: false }}
        classNames={{
          control: (_s) => cn("h-9 pr-4 min-w-fit"),
          valueContainer: (_s) => cn("min-w-fit"),
        }}
        isValidNewOption={(value) => parseTypedTime(value) !== undefined}
        getNewOptionData={(value) =>
          asOption(parseTypedTime(value) ?? { hour: 0, minute: 0 })
        }
      />
      {withAmPm && (
        <ToggleGroup
          type="single"
          onValueChange={(value) => {
            setMeridiem((value || undefined) as "AM" | "PM" | undefined);
            if (!time || value === time.meridiem) return;
            if (onChangeRef.current) {
              const next = { ...time, hour: (time.hour + 12) % 24 };
              const str = [next.hour, next.minute].join(
                (preferSpace ?? time?.preferSpace) ? " " : ":",
              );
              onChangeRef.current(str);
            }
          }}
          disabled={disabled}
          value={meridiem}
          className="flex-shrink-0"
        >
          <ToggleGroupItem value="AM">AM</ToggleGroupItem>
          <ToggleGroupItem value="PM">PM</ToggleGroupItem>
        </ToggleGroup>
      )}
    </div>
  );
};

export default TimeSelector;
